import {Card, Divider} from '@albert-io/atomic';
import {GuessModelV1, QuestionModelV3, QuestionSetModelV1, SubjectModelV2} from '@albert-io/models';
import classNames from 'classnames';
import SplitPane from 'client/components/SplitPane/SplitPane.react';
import {debounce} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import CopyrightModal from 'client/components/CopyrightModal/CopyrightModal.react';

import DraftGuessWrapper from '../../../../AssignmentPracticeView/DraftGuesses/DraftGuessWrapper';

import {
  Explanation,
  GuessCorrectnessBanner,
  InteractivityHandler,
  Prompt,
  ResultIndicatorBar,
  Standards,
  StandardsTable,
  Tags
} from '../ContentBlocks';

import QuestionTypeStore from '../../QuestionType.store';
import {HeadingBar} from '../HeadingBar/HeadingBar.react';
import HowOthersAnswered from '../HowOthersAnswered';
import PostSubmissionScrollPoint from '../PostSubmissionScrollPoint/PostSubmissionScrollPoint';
import '../QuestionWrapper/question-wrapper.scss';
import {
  TranslationHeader,
  useQuestionTranslationStore,
  useShowTranslationToggle
} from '../TranslationSection';
import './question-component.scss';

import {ArchivedQuestionBanner} from 'client/components/ArchivedQuestions/ArchivedQuestionBanner.react';
import CONSTANTS from 'client/constants';

import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

interface PreGuessQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionStore: typeof QuestionTypeStore;
  ActiveQuestionInputArea: React.ComponentType<any>;
  interactive?: boolean;
  isMCQRestylingEnabled?: boolean;
}

const PreGuessQuestionContent = ({
  activeQuestion,
  activeQuestionStore,
  ActiveQuestionInputArea,
  interactive,
  isMCQRestylingEnabled = false
}: PreGuessQuestionContentProps) => {
  return (
    <DraftGuessWrapper storeName={activeQuestionStore.getName()} submitted={false}>
      <InteractivityHandler interactive={interactive}>
        <ActiveQuestionInputArea
          isGuessSubmitted={false}
          isGuessCorrect={false}
          storeName={activeQuestionStore.getName()}
          question={activeQuestion}
          questionId={activeQuestion.getId()}
          isMCQRestylingEnabled={isMCQRestylingEnabled}
        />
      </InteractivityHandler>
    </DraftGuessWrapper>
  );
};

interface PostGuessQuestionContentProps {
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeQuestionStore: typeof QuestionTypeStore;
  activeSubject: SubjectModelV2;
  ActiveQuestionInputArea: React.ComponentType<any>;
  customExplanation?: React.ReactElement; // custom explanation component
  customHOA?: React.ReactElement; // custom how others answered component
  customResultIndicator?: React.ReactElement; // custom result indicator component
  isRedesign?: boolean;
  guess?: GuessModelV1;
  postSubmissionScrollPointRef?: React.RefObject<any>;
  partialCreditEnabled?: boolean;
  isMCQRestylingEnabled?: boolean;
}

const PostGuessQuestionContent = ({
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  isRedesign,
  postSubmissionScrollPointRef,
  partialCreditEnabled = false,
  isMCQRestylingEnabled = false
}: PostGuessQuestionContentProps) => {
  return (
    <DraftGuessWrapper storeName={activeQuestionStore.getName()} submitted>
      <ActiveQuestionInputArea
        isGuessSubmitted
        isGuessCorrect={guess?.isCorrect()}
        storeName={activeQuestionStore.getName()}
        question={activeQuestion}
        questionId={activeQuestion.getId()}
        isMCQRestylingEnabled={isMCQRestylingEnabled}
      />
      <PostSubmissionScrollPoint ref={postSubmissionScrollPointRef} />
      {partialCreditEnabled ? (
        <GuessCorrectnessBanner guess={guess} partialCreditEnabled={partialCreditEnabled} />
      ) : (
        (customResultIndicator ?? <ResultIndicatorBar guess={guess} />)
      )}
      {customExplanation ?? <Explanation question={activeQuestion} />}
      {customHOA ?? <HowOthersAnswered question={activeQuestion} />}
      {!isRedesign && activeSubject.isShouldShowLabelTable() && (
        <StandardsTable question={activeQuestion} />
      )}
      {!isRedesign && (
        <Standards
          question={activeQuestion}
          subject={activeSubject}
          questionSet={activeQuestionSet}
        />
      )}
      {!isRedesign && (
        <Tags question={activeQuestion} subject={activeSubject} questionSet={activeQuestionSet} />
      )}
    </DraftGuessWrapper>
  );
};

interface QuestionContentProps {
  questionState: 'PreGuess' | 'PostGuess';
}

type CombinedProps = QuestionContentProps &
  (PreGuessQuestionContentProps | PostGuessQuestionContentProps);

const QuestionContent = (props: CombinedProps) => {
  const {questionState, ...rest} = props;

  if (questionState === 'PreGuess') {
    const {
      activeQuestion,
      activeQuestionStore,
      ActiveQuestionInputArea,
      interactive,
      isMCQRestylingEnabled
    } = rest as PreGuessQuestionContentProps;
    return (
      <PreGuessQuestionContent
        activeQuestion={activeQuestion}
        activeQuestionStore={activeQuestionStore}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        interactive={interactive}
        isMCQRestylingEnabled={isMCQRestylingEnabled}
      />
    );
  }

  const {
    activeQuestion,
    activeQuestionSet,
    activeQuestionStore,
    activeSubject,
    ActiveQuestionInputArea,
    customExplanation,
    customHOA,
    customResultIndicator,
    guess,
    isRedesign,
    postSubmissionScrollPointRef,
    partialCreditEnabled = false,
    isMCQRestylingEnabled
  } = rest as PostGuessQuestionContentProps;
  return (
    <PostGuessQuestionContent
      activeQuestion={activeQuestion}
      activeQuestionSet={activeQuestionSet}
      activeQuestionStore={activeQuestionStore}
      activeSubject={activeSubject}
      ActiveQuestionInputArea={ActiveQuestionInputArea}
      customExplanation={customExplanation}
      customHOA={customHOA}
      customResultIndicator={customResultIndicator}
      guess={guess}
      isRedesign={isRedesign}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      partialCreditEnabled={partialCreditEnabled}
      isMCQRestylingEnabled={isMCQRestylingEnabled}
    />
  );
};

export interface QuestionComponentProps
  extends QuestionContentProps,
    PreGuessQuestionContentProps,
    PostGuessQuestionContentProps {
  actions: React.ReactElement;
  answerInputPaneRef?: React.RefObject<HTMLDivElement>;
  customHeader?: React.ReactNode;
  displayOrientation: 'default' | 'vertical' | 'horizontal';
  hasHeading?: boolean;
  hasQuestionSetAccessPredicate: (questionSet: QuestionSetModelV1) => boolean;
  headingActions: React.ReactNode;
  isMobile?: boolean;
  modifyTitle?: (args: any) => void;
  navigateBackProps?: {
    disabled: boolean;
    handleNavigation: () => void;
  };
  navigateNextProps?: {
    disabled: boolean;
    handleNavigation: () => void;
  };
  resizerPosition?: number;
  onResize?: (size: number) => void;
  showDifficulty: boolean;
  stackedContainerRef?: React.RefObject<HTMLDivElement>;
  partialCreditEnabled?: boolean;
  shouldSetContentMaxWidth?: boolean;
}

type QuestionComponentContentSplitProps = Omit<
  QuestionComponentProps,
  'displayOrientation' | 'headingActions' | 'showDifficulty'
>;

const PANE_MIN_WIDTH = 280;
const CONTAINER_PADDING = 32;

export const questionStemRegex = /\[question-stem\]([\s\S]*?)\[\/question-stem\]/;
// remove question stem from left, only show question stem on right
const transformLeftText = (text) => text.replace(questionStemRegex, '');
const transformRightText = (text) => text.match(questionStemRegex)?.[1] ?? '';

const QuestionComponentContentSplit = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  answerInputPaneRef,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  hasQuestionSetAccessPredicate,
  interactive,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  questionState,
  resizerPosition,
  partialCreditEnabled = false,
  isMCQRestylingEnabled,
  shouldSetContentMaxWidth
}: QuestionComponentContentSplitProps) => {
  const splitPaneRef = useRef<HTMLDivElement | null>(null);
  // using the width of the split pane to determine the max size of the left pane (in other words, enforcing the min size for the right pane)
  // falling back on using the viewport width minus padding since we can assume that the split pane will be close to the full width of the viewport
  const [elementSize, setElementSize] = useState<number>(window.innerWidth - CONTAINER_PADDING);

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations
    }))
  );

  useEffect(() => {
    const handleResize = () => {
      const newSize =
        splitPaneRef.current?.getBoundingClientRect().width ??
        window.innerWidth - CONTAINER_PADDING;
      setElementSize(newSize);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  const showTranslationHeader = useShowTranslationToggle();
  const {isFeatureEnabled: isNewTranslationToggleEnabled} =
    useIsFeatureFlagEnabled('new_translation_toggle');

  return (
    <div
      ref={splitPaneRef}
      className={classNames('split-content-wrapper h-full', {
        'flex flex-col': showTranslationHeader
      })}
    >
      {!isNewTranslationToggleEnabled && showTranslationHeader && <TranslationHeader />}
      <SplitPane
        allowResize
        primary='first'
        split='vertical'
        maxSize={elementSize - PANE_MIN_WIDTH}
        minSize={PANE_MIN_WIDTH}
        defaultSize={resizerPosition}
        onChange={debounce((size) => onResize?.(size), 100)}
        style={{position: 'relative'}}
        resizerClassName='resizer-grabber question-component-resizer'
        pane1Style={{display: 'flex', flexDirection: 'column', overflow: 'auto'}}
        pane2Style={{display: 'flex', flexDirection: 'column', overflow: 'auto'}}
      >
        <Card
          className={classNames('question-wrapper question-split-pane instructions-pane', {
            'with-translations': showTranslationHeader && !isNewTranslationToggleEnabled
          })}
          roundness={showTranslationHeader ? 'none' : 'small'}
        >
          <div
            className={classNames('question-wrapper__body p-x4 xs:p-x2', {
              'ml-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth
            })}
          >
            <Prompt question={activeQuestion} transformText={transformLeftText} />
          </div>
        </Card>
        <Card
          as='form'
          className={classNames('question-wrapper question-split-pane answer-options-pane', {
            'with-translations': showTranslationHeader && !isNewTranslationToggleEnabled
          })}
          roundness={showTranslationHeader ? 'none' : 'small'}
          // @ts-ignore: TS complains about onSubmit not being a valid prop for a `form` Card
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div
            ref={answerInputPaneRef}
            className='question-wrapper__body flex flex-col justify-between'
          >
            {/* change padding when translation toggle is available */}

            <div
              className={classNames('p-x4 xs:p-x2', {
                'mt-x4': showTranslationHeader && !showTranslations,
                'mt-x8': showTranslationHeader && showTranslations,
                'mr-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth
              })}
            >
              {activeQuestion?.getPrompt()?.match(questionStemRegex) && (
                <div>
                  <Prompt question={activeQuestion} transformText={transformRightText} />
                  <Divider spacing={4} />
                </div>
              )}
              <QuestionContent
                activeQuestion={activeQuestion}
                activeQuestionSet={activeQuestionSet}
                activeQuestionStore={activeQuestionStore}
                activeSubject={activeSubject}
                ActiveQuestionInputArea={ActiveQuestionInputArea}
                customExplanation={customExplanation}
                customHOA={customHOA}
                customResultIndicator={customResultIndicator}
                guess={guess}
                interactive={interactive}
                isRedesign
                postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                questionState={questionState}
                partialCreditEnabled={partialCreditEnabled}
                isMCQRestylingEnabled={isMCQRestylingEnabled}
              />
            </div>
            <CopyrightModal />
            {actions &&
              React.cloneElement(actions, {
                guess,
                hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
                navigateBackProps,
                navigateNextProps,
                question: activeQuestion,
                questionSet: activeQuestionSet,
                subject: activeSubject
              })}
          </div>
        </Card>
      </SplitPane>
    </div>
  );
};

const QuestionComponentHorizontalLayout = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  ActiveQuestionInputArea,
  customExplanation,
  customHOA,
  customResultIndicator,
  guess,
  hasQuestionSetAccessPredicate,
  interactive,
  navigateBackProps,
  navigateNextProps,
  postSubmissionScrollPointRef,
  questionState,
  stackedContainerRef,
  partialCreditEnabled,
  isMCQRestylingEnabled,
  shouldSetContentMaxWidth
}: QuestionComponentContentSplitProps) => {
  const showTranslationHeader = useShowTranslationToggle();
  const {isFeatureEnabled: isNewTranslationToggleEnabled} =
    useIsFeatureFlagEnabled('new_translation_toggle');
  return (
    <div ref={stackedContainerRef} className='question-component__horizontal-layout-wrapper'>
      {!isNewTranslationToggleEnabled && showTranslationHeader && <TranslationHeader />}
      <div className={'question-component__horizontal-layout__question-wrapper'}>
        <Card
          roundness='none'
          className={classNames('question-wrapper horizontal-layout__instructions-card', {
            'with-translations': showTranslationHeader && !isNewTranslationToggleEnabled
          })}
        >
          <div
            className={classNames('question-wrapper__body p-x4 xs:p-x2', {
              'ml-auto! w-full max-w-[600px]!': shouldSetContentMaxWidth
            })}
          >
            {/* change padding when translation toggle is avialbe */}
            <Prompt question={activeQuestion} transformText={transformLeftText} />
          </div>
        </Card>
        <Card
          as='form'
          className='question-wrapper horizontal-layout__answer-options-card'
          // @ts-ignore: TS complains about onSubmit not being a valid prop for a `form` Card
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div
            className={classNames('question-wrapper__body', {
              'max-w-[600px]': shouldSetContentMaxWidth
            })}
          >
            <div className='p-x4 xs:p-x2'>
              {activeQuestion?.getPrompt()?.match(questionStemRegex) && (
                <div>
                  <Prompt question={activeQuestion} transformText={transformRightText} />
                  <Divider spacing={4} />
                </div>
              )}
              <QuestionContent
                activeQuestion={activeQuestion}
                activeQuestionSet={activeQuestionSet}
                activeQuestionStore={activeQuestionStore}
                activeSubject={activeSubject}
                ActiveQuestionInputArea={ActiveQuestionInputArea}
                customExplanation={customExplanation}
                customHOA={customHOA}
                customResultIndicator={customResultIndicator}
                guess={guess}
                interactive={interactive}
                isRedesign
                postSubmissionScrollPointRef={postSubmissionScrollPointRef}
                questionState={questionState}
                partialCreditEnabled={partialCreditEnabled}
                isMCQRestylingEnabled={isMCQRestylingEnabled}
              />
            </div>
            <CopyrightModal />
            {actions &&
              React.cloneElement(actions, {
                guess,
                hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
                navigateBackProps,
                navigateNextProps,
                question: activeQuestion,
                questionSet: activeQuestionSet,
                subject: activeSubject
              })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export const QuestionComponent = ({
  actions,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  answerInputPaneRef,
  ActiveQuestionInputArea,
  customExplanation,
  customHeader,
  customHOA,
  customResultIndicator,
  displayOrientation,
  guess,
  hasHeading = true,
  hasQuestionSetAccessPredicate,
  headingActions,
  interactive,
  isMobile,
  modifyTitle,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  questionState,
  resizerPosition,
  showDifficulty,
  stackedContainerRef,
  partialCreditEnabled,
  isMCQRestylingEnabled,
  shouldSetContentMaxWidth = false
}: QuestionComponentProps) => {
  const {init, reset, isReady, fetchTranslation, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      init: state.init,
      reset: state.reset,
      fetchTranslation: state.fetchTranslation,
      isReady: state.isReady,
      showTranslations: state.showTranslations
    }))
  );

  const {isFeatureEnabled: isNewTranslationToggleEnabled} =
    useIsFeatureFlagEnabled('new_translation_toggle');

  const isArchivedQuestion = activeQuestion['status'] === CONSTANTS.QUESTIONSET_STATUSES.ARCHIVED;

  const [shouldShowHeader, setShouldShowHeader] = useState(hasHeading);

  // initialize translation store, check if translation toggle should be shown
  useEffect(() => {
    if (!isReady) {
      const params = global.location.pathname.split('/');

      let assignmentId: string | null = null;
      let isAdaptive = false;

      if (params.some((param) => param === 'assignment')) {
        assignmentId = params[params.length - 1];
        setShouldShowHeader(false);
      }

      if (params.some((param) => param === 'adaptive')) {
        isAdaptive = true;
      }

      init(assignmentId, isAdaptive);
    }
  }, [init, isReady]);

  useEffect(() => {
    if (!activeQuestion?.getId()) {
      return;
    }

    // the translation store handles early returning if translations are not meant to be shown
    // safe to call here
    fetchTranslation(activeQuestion, activeSubject);
  }, [activeQuestion, fetchTranslation, activeQuestionSet, activeSubject]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  // header should never show in AB
  useEffect(() => {
    const params = global.location.pathname.split('/');

    if (params.some((param) => param === 'assignment-builder')) {
      setShouldShowHeader(false);
    }
  }, []);

  // -------- Student views ------------

  if (displayOrientation === 'vertical' && !isMobile) {
    return (
      <QuestionComponentContentSplit
        actions={actions}
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeQuestionStore={activeQuestionStore}
        activeSubject={activeSubject}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        answerInputPaneRef={answerInputPaneRef}
        customExplanation={customExplanation}
        customHOA={customHOA}
        customResultIndicator={customResultIndicator}
        guess={guess}
        hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
        interactive={interactive}
        isMobile={isMobile}
        navigateBackProps={navigateBackProps}
        navigateNextProps={navigateNextProps}
        onResize={onResize}
        postSubmissionScrollPointRef={postSubmissionScrollPointRef}
        questionState={questionState}
        resizerPosition={resizerPosition}
        isMCQRestylingEnabled={isMCQRestylingEnabled}
        partialCreditEnabled={partialCreditEnabled}
        shouldSetContentMaxWidth={shouldSetContentMaxWidth}
      />
    );
  }

  if (
    (displayOrientation === 'horizontal' && !isMobile) ||
    (isMobile && displayOrientation !== 'default')
  ) {
    return (
      <QuestionComponentHorizontalLayout
        actions={actions}
        activeQuestion={activeQuestion}
        activeQuestionSet={activeQuestionSet}
        activeQuestionStore={activeQuestionStore}
        activeSubject={activeSubject}
        ActiveQuestionInputArea={ActiveQuestionInputArea}
        customExplanation={customExplanation}
        customHOA={customHOA}
        customResultIndicator={customResultIndicator}
        guess={guess}
        hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
        interactive={interactive}
        isMobile={isMobile}
        navigateBackProps={navigateBackProps}
        navigateNextProps={navigateNextProps}
        postSubmissionScrollPointRef={postSubmissionScrollPointRef}
        questionState={questionState}
        stackedContainerRef={stackedContainerRef}
        isMCQRestylingEnabled={isMCQRestylingEnabled}
        partialCreditEnabled={partialCreditEnabled}
        shouldSetContentMaxWidth={shouldSetContentMaxWidth}
      />
    );
  }

  return (
    <Card
      as='form'
      className='question-wrapper'
      // @ts-ignore: TS complains about onSubmit not being a valid prop for a `form` Card
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className='question-wrapper__heading'>
        {shouldShowHeader && (
          <>
            <HeadingBar
              question={activeQuestion}
              questionSet={activeQuestionSet}
              navigateBackProps={navigateBackProps}
              navigateNextProps={navigateNextProps}
              headingActions={headingActions}
              modifyTitle={modifyTitle}
              showDifficulty={showDifficulty}
              customHeader={customHeader}
            />
            {!isNewTranslationToggleEnabled && <TranslationHeader inLegacyView />}
            {/* <TranslationHeader inLegacyView /> */}
          </>
        )}
      </div>
      <div className='question-wrapper__body'>
        <div
          className={classNames('p-x4 xs:p-x2', {
            'mt-x2': showTranslations && !isNewTranslationToggleEnabled
          })}
        >
          {isArchivedQuestion && <ArchivedQuestionBanner classname='mb-x4' />}
          <Prompt question={activeQuestion} />
          <QuestionContent
            activeQuestion={activeQuestion}
            activeQuestionSet={activeQuestionSet}
            activeQuestionStore={activeQuestionStore}
            activeSubject={activeSubject}
            ActiveQuestionInputArea={ActiveQuestionInputArea}
            customExplanation={customExplanation}
            customHOA={customHOA}
            customResultIndicator={customResultIndicator}
            guess={guess}
            interactive={interactive}
            isRedesign={false}
            postSubmissionScrollPointRef={postSubmissionScrollPointRef}
            questionState={questionState}
            partialCreditEnabled={partialCreditEnabled}
            isMCQRestylingEnabled={isMCQRestylingEnabled}
          />
        </div>
        {actions &&
          React.cloneElement(actions, {
            guess,
            hasAccess: hasQuestionSetAccessPredicate?.(activeQuestionSet) || false,
            navigateBackProps,
            navigateNextProps,
            question: activeQuestion,
            questionSet: activeQuestionSet,
            subject: activeSubject
          })}
      </div>
    </Card>
  );
};
